import React, { useState, useCallback, useEffect } from "react";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { firestoreProd } from "../../firebase";
import { apiUrl } from "../../globalVariables";
import "./ProdDeployment.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const TESTING_HANDBOOK_URL =
  "https://docs.google.com/document/d/139gKe9e6NXTZIWi9khhRZG0HZuIxiK-hedfcWXu6HZQ/edit?tab=t.0";

// Single consolidated pre-deployment testing workflow (backend repo, dev branch).
// Runs E2E Full + E2E Atomic in parallel, then the pipeline smoke test.
const PROD_DEPLOYMENT_TESTING_URL =
  "https://github.com/addy-ai/backend/actions/workflows/prod-deployment-testing.yml";

// Backend admin API URLs — auto-switch between local emulator and prod via globalVariables
const CHANGELOG_API_URL      = `${apiUrl}/admin/prod-deployment/generate-changelog`;
const JIRA_TAG_API_URL        = `${apiUrl}/admin/prod-deployment/tag-jira`;
const TESTING_GUIDE_API_URL   = `${apiUrl}/admin/prod-deployment/generate-testing-guide`;

const TESTING_PLAYBOOK_DOC_URL =
  "https://docs.google.com/document/d/139gKe9e6NXTZIWi9khhRZG0HZuIxiK-hedfcWXu6HZQ/edit";

const PR_REPOS = [
  { key: "backend",    label: "Backend",          repo: "addy-ai/backend" },
  { key: "client",     label: "Client (Frontend)", repo: "addy-ai/client" },
  { key: "chrome-ext", label: "Chrome Extension",  repo: "addy-ai/chrome-extension" },
  { key: "sidebar",    label: "Sidebar Chat",       repo: "addy-ai/sidebar-chat" },
  { key: "gmail",      label: "Gmail Server",       repo: "addy-ai/gmail-server" },
  { key: "microsoft",  label: "Microsoft Server",   repo: "addy-ai/microsoft-server" },
];

const FIRESTORE_COLLECTION = "prodDeployments";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayLabel() {
  const d = new Date();
  return `PROD_${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
}

function fmtDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function genId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const ISSUE_SEVERITY_OPTIONS = ["low", "medium", "high", "critical"];
const ISSUE_SEVERITY_LABELS  = { low: "Low", medium: "Medium", high: "High", critical: "Critical" };

// ─── Shared UI atoms ──────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    idle:       { label: "Idle",        cls: "idle" },
    running:    { label: "Running…",    cls: "running" },
    passed:     { label: "Passed",      cls: "passed" },
    failed:     { label: "Failed",      cls: "failed" },
    completed:  { label: "Completed",   cls: "passed" },
    inprogress: { label: "In Progress", cls: "running" },
  };
  const { label, cls } = map[status] || map.idle;
  return <span className={`prod-status-badge ${cls}`}>{label}</span>;
}

function StepNumber({ n, status }) {
  const cls = status === "passed" || status === "completed" ? "completed"
    : status === "running" || status === "inprogress" ? "running" : "";
  return (
    <div className={`prod-step-number ${cls}`}>
      {(status === "passed" || status === "completed") ? "✓" : n}
    </div>
  );
}

// Reusable "Mark as passed" override button shown on every step
function MarkPassedBtn({ done, onMark }) {
  if (done) return null;
  return (
    <button
      className="btn btn-sm btn-outline-secondary prod-mark-passed-btn"
      onClick={onMark}
      title="Mark this step as passed manually"
    >
      ✓ Mark as passed
    </button>
  );
}

// Renders markdown via react-markdown + GFM for high-quality display:
// proper headings, lists, tables, code fences, and clickable links that open
// in a new tab. Styled via .prod-markdown-block in ProdDeployment.css.
function MarkdownBlock({ content }) {
  if (!content) return null;
  return (
    <div className="prod-markdown-block">
      <ReactMarkdown remarkPlugins={[remarkGfm]} linkTarget="_blank">
        {content}
      </ReactMarkdown>
    </div>
  );
}

function IssueSeverityBadge({ severity }) {
  const map = {
    low:      { label: "Low",      cls: "issue-sev-low" },
    medium:   { label: "Medium",   cls: "issue-sev-medium" },
    high:     { label: "High",     cls: "issue-sev-high" },
    critical: { label: "Critical", cls: "issue-sev-critical" },
  };
  const { label, cls } = map[severity] || map.low;
  return <span className={`dep-issue-badge ${cls}`}>{label}</span>;
}

// ─── Issues Section (used in wizard + detail modal) ───────────────────────────

function IssuesSection({ issues = [], deploymentId, onChange }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", severity: "medium", jiraTicket: "", description: "" });
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleAdd = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const newIssue = {
      id: genId(),
      title: form.title.trim(),
      severity: form.severity,
      jiraTicket: form.jiraTicket.trim(),
      description: form.description.trim(),
      resolved: false,
      createdAt: new Date().toISOString(),
    };
    const updated = [...issues, newIssue];
    if (deploymentId) {
      await updateDoc(doc(firestoreProd, FIRESTORE_COLLECTION, deploymentId), {
        issues: updated,
        updatedAt: serverTimestamp(),
      }).catch(() => {});
    }
    onChange?.(updated);
    setForm({ title: "", severity: "medium", jiraTicket: "", description: "" });
    setShowForm(false);
    setSaving(false);
  };

  const toggleResolved = async (issueId) => {
    const updated = issues.map((i) =>
      i.id === issueId ? { ...i, resolved: !i.resolved, resolvedAt: !i.resolved ? new Date().toISOString() : null } : i
    );
    if (deploymentId) {
      await updateDoc(doc(firestoreProd, FIRESTORE_COLLECTION, deploymentId), {
        issues: updated,
        updatedAt: serverTimestamp(),
      }).catch(() => {});
    }
    onChange?.(updated);
  };

  const handleDelete = async (issueId) => {
    const updated = issues.filter((i) => i.id !== issueId);
    if (deploymentId) {
      await updateDoc(doc(firestoreProd, FIRESTORE_COLLECTION, deploymentId), {
        issues: updated,
        updatedAt: serverTimestamp(),
      }).catch(() => {});
    }
    onChange?.(updated);
  };

  return (
    <div className="dep-issues-section">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="prod-section-header">
          Issues
          {issues.length > 0 && (
            <span className="dep-issues-count ms-2">{issues.filter((i) => !i.resolved).length} open</span>
          )}
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowForm((p) => !p)}>
          {showForm ? "Cancel" : "+ Add Issue"}
        </button>
      </div>

      {showForm && (
        <div className="dep-issue-form">
          <div className="dep-issue-form-row">
            <input
              className="dep-issue-input"
              placeholder="Issue title *"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              autoFocus
            />
            <select
              className="dep-issue-select"
              value={form.severity}
              onChange={(e) => setField("severity", e.target.value)}
            >
              {ISSUE_SEVERITY_OPTIONS.map((s) => (
                <option key={s} value={s}>{ISSUE_SEVERITY_LABELS[s]}</option>
              ))}
            </select>
          </div>
          <div className="dep-issue-form-row">
            <input
              className="dep-issue-input"
              placeholder="Jira ticket (e.g. ADDY-1234)"
              value={form.jiraTicket}
              onChange={(e) => setField("jiraTicket", e.target.value)}
            />
          </div>
          <textarea
            className="dep-issue-input dep-issue-textarea"
            placeholder="Description (optional)"
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
            rows={2}
          />
          <div className="d-flex justify-content-end gap-2 mt-2">
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button
              className="btn btn-sm btn-danger"
              onClick={handleAdd}
              disabled={saving || !form.title.trim()}
            >
              {saving ? "Saving…" : "Add Issue"}
            </button>
          </div>
        </div>
      )}

      {issues.length === 0 && !showForm ? (
        <div className="dep-issues-empty">No issues logged for this deployment.</div>
      ) : (
        <div className="dep-issues-list">
          {issues.map((issue) => (
            <div key={issue.id} className={`dep-issue-item ${issue.resolved ? "dep-issue-item--resolved" : ""}`}>
              <div className="dep-issue-item-header">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <IssueSeverityBadge severity={issue.severity} />
                  <span className={`dep-issue-title ${issue.resolved ? "dep-issue-title--resolved" : ""}`}>
                    {issue.title}
                  </span>
                  {issue.jiraTicket && (
                    <span className="dep-issue-jira">🎫 {issue.jiraTicket}</span>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button
                    className={`btn btn-sm ${issue.resolved ? "btn-outline-secondary" : "btn-outline-success"}`}
                    onClick={() => toggleResolved(issue.id)}
                    title={issue.resolved ? "Mark as open" : "Mark as resolved"}
                    style={{ fontSize: "0.72rem", padding: "2px 8px" }}
                  >
                    {issue.resolved ? "↩ Reopen" : "✓ Resolve"}
                  </button>
                  <button
                    className="dep-delete-btn"
                    onClick={() => handleDelete(issue.id)}
                    title="Remove issue"
                  >
                    ✕
                  </button>
                </div>
              </div>
              {issue.description && (
                <div className="dep-issue-desc">{issue.description}</div>
              )}
              {issue.resolved && issue.resolvedAt && (
                <div className="dep-issue-resolved-at">
                  Resolved {new Date(issue.resolvedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Deployment History Card ──────────────────────────────────────────────────

function DeploymentCard({ dep, onOpen, onContinue, onDelete }) {
  const checks = dep.checks || {};
  const checkCount = Object.values(checks).filter(Boolean).length;
  const totalChecks = 5;
  const isInProgress = dep.status !== "completed";
  const [confirmDelete, setConfirmDelete] = useState(false);
  const openIssues = (dep.issues || []).filter((i) => !i.resolved).length;
  const totalIssues = (dep.issues || []).length;

  const handleDeleteClick = (e) => { e.stopPropagation(); setConfirmDelete(true); };
  const handleConfirmDelete = (e) => { e.stopPropagation(); onDelete(dep.id); };
  const handleCancelDelete = (e) => { e.stopPropagation(); setConfirmDelete(false); };

  const handleCardClick = () => {
    if (isInProgress) onContinue(dep);
    else onOpen(dep);
  };

  return (
    <div className={`dep-card ${isInProgress ? "dep-card--inprogress" : ""}`} onClick={handleCardClick}>
      <div className="dep-card-header">
        <div>
          <div className="dep-card-label">{dep.label || "Deployment"}</div>
          <div className="dep-card-date">{fmtDate(dep.createdAt)}</div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <StatusBadge status={dep.status || "inprogress"} />
          {!confirmDelete ? (
            <button className="dep-delete-btn" onClick={handleDeleteClick} title="Delete deployment">✕</button>
          ) : (
            <div className="dep-delete-confirm" onClick={(e) => e.stopPropagation()}>
              <span style={{ fontSize: "0.72rem", color: "#dc3545" }}>Delete?</span>
              <button className="dep-delete-confirm-yes" onClick={handleConfirmDelete}>Yes</button>
              <button className="dep-delete-confirm-no" onClick={handleCancelDelete}>No</button>
            </div>
          )}
        </div>
      </div>

      <div className="dep-card-checks">
        {[
          { key: "smokeTest", label: "Smoke" },
          { key: "e2e",       label: "E2E" },
          { key: "prsMerged", label: "PRs" },
          { key: "meeting",   label: "Meeting" },
          { key: "changelog", label: "Changelog" },
        ].map(({ key, label }) => (
          <span key={key} className={`dep-check-pill ${checks[key] ? "done" : "pending"}`}>
            {checks[key] ? "✓" : "○"} {label}
          </span>
        ))}
      </div>

      <div className="dep-progress-bar">
        <div className="dep-progress-fill" style={{ width: `${(checkCount / totalChecks) * 100}%` }} />
      </div>
      <div className="dep-progress-label">{checkCount}/{totalChecks} checks complete</div>

      {totalIssues > 0 && (
        <div className={`dep-card-issues-hint ${openIssues > 0 ? "dep-card-issues-hint--open" : "dep-card-issues-hint--resolved"}`}>
          {openIssues > 0 ? `⚠ ${openIssues} open issue${openIssues !== 1 ? "s" : ""}` : `✓ ${totalIssues} issue${totalIssues !== 1 ? "s" : ""} resolved`}
        </div>
      )}

      {dep.changelogComplete && (
        <div className="dep-card-changelog-hint">📋 Changelog available</div>
      )}

      {isInProgress && (
        <div className="dep-card-continue-hint">▶ Click to continue →</div>
      )}
    </div>
  );
}

// ─── Deployment Detail Modal ──────────────────────────────────────────────────

function DeploymentDetailModal({ dep, onClose, onIssuesChange }) {
  const [tab, setTab] = useState("overview");
  const [localIssues, setLocalIssues] = useState(dep.issues || []);
  if (!dep) return null;

  const openIssues = localIssues.filter((i) => !i.resolved).length;

  const handleIssuesChange = (updated) => {
    setLocalIssues(updated);
    onIssuesChange?.(dep.id, updated);
  };

  return (
    <div className="dep-modal-backdrop" onClick={onClose}>
      <div className="dep-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dep-modal-header">
          <div>
            <div className="fw-bold" style={{ fontSize: "1rem" }}>{dep.label}</div>
            <div className="text-muted" style={{ fontSize: "0.8rem" }}>{fmtDate(dep.createdAt)}</div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <StatusBadge status={dep.status || "inprogress"} />
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Modal tabs */}
        <div className="dep-modal-tabs">
          {[
            { key: "overview",  label: "Overview" },
            { key: "issues",    label: `Issues${openIssues > 0 ? ` (${openIssues})` : ""}` },
            { key: "changelog", label: "Changelog" },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`dep-modal-tab ${tab === key ? "active" : ""}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="dep-modal-body">
          {/* Overview tab */}
          {tab === "overview" && (
            <>
              <div className="prod-section-header mb-2">Deployment Checks</div>
              <div className="dep-checks-grid mb-3">
                {[
                  { key: "smokeTest", label: "Smoke Test", icon: "🔥" },
                  { key: "e2e",       label: "E2E Tests",  icon: "🤖" },
                  { key: "prsMerged", label: "PRs Merged", icon: "🔀" },
                  { key: "meeting",   label: "Meeting",    icon: "🤝" },
                  { key: "changelog", label: "Changelog",  icon: "📋" },
                ].map(({ key, label, icon }) => {
                  const done = dep.checks?.[key];
                  return (
                    <div key={key} className={`dep-check-item ${done ? "done" : "pending"}`}>
                      <span>{icon}</span>
                      <span>{label}</span>
                      <span className="ms-auto">{done ? "✓" : "—"}</span>
                    </div>
                  );
                })}
              </div>

              {dep.prs && dep.prs.some(p => p.prUrl) && (
                <>
                  <div className="prod-section-header mb-2">Pull Requests</div>
                  <div className="d-flex flex-column gap-1 mb-3">
                    {dep.prs.filter(p => p.prUrl).map((pr) => (
                      <div key={pr.key} className="dep-pr-row">
                        <span className="fw-medium" style={{ minWidth: 140 }}>{pr.label}</span>
                        <a href={pr.prUrl} target="_blank" rel="noopener noreferrer" className="dep-pr-link">{pr.prUrl}</a>
                        {pr.merged && <span className="badge bg-success ms-auto">Merged</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* Issues tab */}
          {tab === "issues" && (
            <IssuesSection
              issues={localIssues}
              deploymentId={dep.id}
              onChange={handleIssuesChange}
            />
          )}

          {/* Changelog tab */}
          {tab === "changelog" && (
            <>
              {(dep.changelogComplete || dep.changelogClient) ? (
                <>
                  <div className="dep-changelog-tabs mb-2">
                    <button className={`dep-changelog-tab ${tab === "complete" ? "active" : ""}`} onClick={() => setTab("complete")}>
                      Complete (Internal)
                    </button>
                    <button className={`dep-changelog-tab ${tab === "client" ? "active" : ""}`} onClick={() => setTab("client")}>
                      Client-Facing
                    </button>
                  </div>
                  <ChangelogTabContent dep={dep} />
                </>
              ) : (
                <div className="text-muted" style={{ fontSize: "0.85rem", padding: "16px 0" }}>
                  No changelog generated for this deployment.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ChangelogTabContent({ dep }) {
  const [innerTab, setInnerTab] = useState("complete");
  return (
    <>
      <div className="dep-changelog-tabs mb-2">
        <button className={`dep-changelog-tab ${innerTab === "complete" ? "active" : ""}`} onClick={() => setInnerTab("complete")}>
          Complete (Internal)
        </button>
        <button className={`dep-changelog-tab ${innerTab === "client" ? "active" : ""}`} onClick={() => setInnerTab("client")}>
          Client-Facing
        </button>
      </div>
      {innerTab === "complete" && dep.changelogComplete && (
        <>
          <MarkdownBlock content={dep.changelogComplete} />
          <button className="btn btn-sm btn-outline-secondary mt-2"
            onClick={() => navigator.clipboard.writeText(dep.changelogComplete)}>Copy</button>
        </>
      )}
      {innerTab === "client" && dep.changelogClient && (
        <>
          <MarkdownBlock content={dep.changelogClient} />
          <button className="btn btn-sm btn-outline-secondary mt-2"
            onClick={() => navigator.clipboard.writeText(dep.changelogClient)}>Copy</button>
        </>
      )}
    </>
  );
}

// ─── Wizard Steps ─────────────────────────────────────────────────────────────

// Single consolidated pre-deployment testing step. Replaces the previous
// separate "Smoke Test" + "Full E2E" steps with one button that opens the
// new `prod-deployment-testing.yml` workflow on GitHub. That workflow runs
// E2E Full + Atomic in parallel and then the pipeline smoke test, always
// against the `dev` branch. We still flip both `checks.smokeTest` and
// `checks.e2e` on the deployment doc so existing dashboard cards,
// analytics, and historical data keep working unchanged.
function ProdDeploymentTestingStep({ onComplete, initialDone }) {
  const [done, setDone] = useState(!!initialDone);
  const markDone = () => { setDone(true); onComplete?.(); };

  return (
    <div className={`prod-step-card p-3 mb-3 ${done ? "prod-step-card--done" : ""}`}>
      <div className="d-flex align-items-start gap-3">
        <StepNumber n={1} status={done ? "passed" : "idle"} />
        <div className="flex-grow-1">
          <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
            <div>
              <div className="fw-semibold">Run Prod Deployment Testing</div>
              <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                One workflow runs <strong>E2E Full + Atomic in parallel</strong>, then the
                <strong> pipeline smoke test</strong>. Trigger on the <code>dev</code> branch.
                Per-test breakdowns are posted to Slack automatically.
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <MarkPassedBtn done={done} onMark={markDone} />
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <a href={PROD_DEPLOYMENT_TESTING_URL} target="_blank" rel="noopener noreferrer"
              className="btn btn-sm btn-primary">▶ Run Workflow on GitHub Actions ↗</a>
            {done && <span className="badge bg-success">Passed ✓</span>}
          </div>
          {!done && (
            <div className="prod-smoke-instructions mt-3">
              <div className="prod-section-header">How to run</div>
              <ol className="mb-0" style={{ fontSize: "0.82rem", paddingLeft: "1.2rem" }}>
                <li>Click <strong>Run Workflow on GitHub Actions ↗</strong> above.</li>
                <li>Click <strong>Run workflow</strong> (top-right of the workflow list).</li>
                <li>Select the <code>dev</code> branch.</li>
                <li>Click the green <strong>Run workflow</strong> button.</li>
                <li>
                  Wait for all three jobs to pass:{" "}
                  <em>E2E Tests — Full Suite</em>, <em>E2E Tests — Atomic Suite</em>, <em>Smoke Test</em>.
                  The E2E suites post per-test results to Slack.
                </li>
                <li>Once all jobs are green, click <strong>Mark as passed</strong>.</li>
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PRChecklistStep({ onComplete, onChange, initialDone, initialPrs }) {
  const [prs, setPrs] = useState(() => {
    // On resume, restore saved PR URLs/merged state; fall back to defaults
    if (initialPrs && initialPrs.length > 0) {
      return PR_REPOS.map((r) => {
        const saved = initialPrs.find((p) => p.key === r.key);
        return saved ? { ...r, merged: !!saved.merged, prUrl: saved.prUrl || "" } : { ...r, merged: false, prUrl: "" };
      });
    }
    return PR_REPOS.map((r) => ({ ...r, merged: false, prUrl: "" }));
  });
  const [manualDone, setManualDone] = useState(!!initialDone);

  const allMerged = prs.every((p) => p.merged);
  const isDone = manualDone || allMerged;

  useEffect(() => { if (allMerged) onComplete?.(); }, [allMerged]); // eslint-disable-line
  useEffect(() => { onChange?.(prs); }, [prs]); // eslint-disable-line

  const markPassed = () => { setManualDone(true); onComplete?.(); };
  const toggle = (key) => setPrs((p) => p.map((r) => r.key === key ? { ...r, merged: !r.merged } : r));
  const setUrl = (key, v) => setPrs((p) => p.map((r) => r.key === key ? { ...r, prUrl: v } : r));

  return (
    <div className={`prod-step-card p-3 mb-3 ${isDone ? "prod-step-card--done" : ""}`}>
      <div className="d-flex align-items-start gap-3">
        <StepNumber n={2} status={isDone ? "passed" : "idle"} />
        <div className="flex-grow-1">
          <div className="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-2">
            <div className="fw-semibold">Ready & Merge PRs</div>
            <MarkPassedBtn done={isDone} onMark={markPassed} />
          </div>
          <div className="text-muted mb-3" style={{ fontSize: "0.8rem" }}>
            Ensure all relevant PRs are reviewed and merged before deployment.
          </div>
          <div className="d-flex flex-column gap-2">
            {prs.map((pr) => (
              <div key={pr.key} className={`prod-pr-item ${pr.merged ? "merged" : "pending"}`}>
                <input type="checkbox" className="form-check-input m-0" checked={pr.merged}
                  onChange={() => toggle(pr.key)} style={{ cursor: "pointer" }} />
                <span className="fw-medium" style={{ minWidth: 160 }}>{pr.label}</span>
                <input type="text" className="form-control form-control-sm"
                  placeholder="PR URL (optional)" value={pr.prUrl}
                  onChange={(e) => setUrl(pr.key, e.target.value)} style={{ maxWidth: 320 }} />
                {pr.prUrl && (
                  <a href={pr.prUrl} target="_blank" rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-secondary">Open ↗</a>
                )}
                {pr.merged && <span className="badge bg-success ms-auto">Merged</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Small inline row used by ChangelogStep to show per-substep progress inside
// the consolidated "Run Release Automation" flow. Keeps the UI compact and
// surface-level — users can click Details to see raw output per substep.
function ReleaseSubstepRow({ icon, title, subtitle, status, error, onRetry, rightSlot, children }) {
  const [expanded, setExpanded] = useState(false);

  const statusPill = (() => {
    if (status === "running") {
      return <span className="prod-status-badge running"><span className="prod-spinner prod-spinner--sm me-1" />Running</span>;
    }
    if (status === "done") return <span className="prod-status-badge passed">✓ Done</span>;
    if (status === "failed") return <span className="prod-status-badge failed">✗ Failed</span>;
    return <span className="prod-status-badge idle">Pending</span>;
  })();

  return (
    <div className={`dep-release-row dep-release-row--${status || "idle"}`}>
      <div className="dep-release-row-head">
        <div className="dep-release-row-icon">{icon}</div>
        <div className="flex-grow-1">
          <div className="dep-release-row-title">{title}</div>
          {subtitle && <div className="dep-release-row-subtitle">{subtitle}</div>}
          {status === "failed" && error && (
            <div className="dep-release-row-error">{error}</div>
          )}
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {statusPill}
          {rightSlot}
          {status === "failed" && onRetry && (
            <button className="btn btn-sm btn-outline-secondary" onClick={onRetry}>
              ↺ Retry
            </button>
          )}
          {children && (
            <button
              className="btn btn-sm btn-link p-0"
              style={{ fontSize: "0.75rem" }}
              onClick={() => setExpanded((p) => !p)}
            >
              {expanded ? "Hide details" : "Details"}
            </button>
          )}
        </div>
      </div>
      {expanded && children && <div className="dep-release-row-body">{children}</div>}
    </div>
  );
}

function DeploymentMeetingStep({ onComplete, initialDone }) {
  const [done, setDone] = useState(!!initialDone);
  const markDone = () => { setDone(true); onComplete?.(); };

  return (
    <div className={`prod-step-card p-3 mb-3 ${done ? "prod-step-card--done" : ""}`}>
      <div className="d-flex align-items-start gap-3">
        <StepNumber n={3} status={done ? "passed" : "idle"} />
        <div className="flex-grow-1">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <div className="fw-semibold">Join Deployment Meeting</div>
              <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                Join once all PRs are merged and tests pass. Use the testing handbook below.
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              {done
                ? <span className="badge bg-success">Joined ✓</span>
                : <button className="btn btn-sm btn-success" onClick={markDone}>Mark as Joined</button>
              }
            </div>
          </div>
          <a href={TESTING_HANDBOOK_URL} target="_blank" rel="noopener noreferrer"
            className="prod-handbook-card mt-3 d-flex">
            <span style={{ fontSize: "1.2rem" }}>📋</span>
            <div>
              <div className="fw-semibold" style={{ fontSize: "0.85rem" }}>Testing Handbook</div>
              <div style={{ fontSize: "0.75rem", opacity: 0.8 }}>docs.google.com — Deployment testing guide</div>
            </div>
            <span className="ms-auto" style={{ fontSize: "0.75rem", opacity: 0.7 }}>Open ↗</span>
          </a>
        </div>
      </div>
    </div>
  );
}

// ChangelogStep — now a single-click orchestrator that runs, in order:
//   1. Tag Jira stories with the PROD_ label
//   2. Generate the focused testing playbook (inserts new page in Google Doc)
//   3. Generate both changelogs (internal + client-facing)
//
// Each substep has its own status + retry. The main button runs them all
// in sequence so the user only needs to click once. Individual retries are
// only exposed if a step fails.
export function ChangelogStep({
  deploymentId, label, prs, onComplete,
  initialDone, initialChangelogs, initialJiraTickets,
}) {
  const prodLabel = label || todayLabel();

  const [overallRunning, setOverallRunning] = useState(false);

  // Full Jira ticket manifest for this deployment. Populated by `runTagging`
  // from the tag-jira endpoint's response, and forwarded on subsequent calls
  // to testing-guide + changelog. Bypassing the label-based JQL search this
  // way avoids Jira's Lucene index lag (labels just written via the REST API
  // are not searchable for seconds-to-minutes afterwards), which was causing
  // "0 tickets" in the Testing Playbook right after tagging.
  const [jiraTickets, setJiraTickets] = useState(() =>
    Array.isArray(initialJiraTickets) ? initialJiraTickets : [],
  );

  // Per-substep state: status ∈ { idle | running | done | failed }
  const [tagState, setTagState] = useState({ status: "idle", error: "", result: null, lines: [] });
  const [guideState, setGuideState] = useState({
    status: "idle", error: "", text: "", docUrl: "",
    prCount: 0, jiraStoryCount: 0,
  });
  const [changelogState, setChangelogState] = useState({
    status: initialChangelogs?.complete || initialChangelogs?.client ? "done" : "idle",
    error: "",
    complete: initialChangelogs?.complete || "",
    client: initialChangelogs?.client || "",
    ticketCount: 0,
  });
  const [changelogTab, setChangelogTab] = useState("complete");

  const [manualDone, setManualDone] = useState(!!initialDone);
  const isDone =
    manualDone ||
    (tagState.status === "done" && guideState.status === "done" && changelogState.status === "done");

  const markPassed = () => { setManualDone(true); onComplete?.(); };

  // Individual runners — each can be called standalone for retry. runTagging
  // resolves the Jira ticket manifest and returns it (or null on failure) so
  // the orchestrator can forward it directly to the dependent steps.
  const runTagging = useCallback(async () => {
    setTagState({ status: "running", error: "", result: null, lines: [
      { text: `▶ Tagging unlabelled Jira stories with ${prodLabel}…`, type: "info", id: Math.random() },
    ] });
    try {
      const res = await fetch(JIRA_TAG_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label: prodLabel }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const returnedTickets = Array.isArray(json.tickets) ? json.tickets : [];
      const lines = [
        { text: `▶ Tagging unlabelled Jira stories with ${prodLabel}…`, type: "info", id: Math.random() },
        { text: `✔ Tagged ${json.updated ?? 0} stories.`, type: "success", id: Math.random() },
      ];
      if (json.skipped) {
        lines.push({ text: `  Skipped ${json.skipped} already-labelled stories.`, type: "info", id: Math.random() });
      }
      if (returnedTickets.length > 0) {
        lines.push({
          text: `  Resolved full manifest of ${returnedTickets.length} ticket${returnedTickets.length !== 1 ? "s" : ""} for this deployment.`,
          type: "info",
          id: Math.random(),
        });
      }

      // Capture the manifest in React state so testing-guide + changelog can
      // forward it directly (no Jira label search needed downstream).
      setJiraTickets(returnedTickets);

      // Also persist to Firestore so the manifest survives a page refresh or
      // resume. If writing fails we continue — the in-memory state still works
      // for this session.
      if (deploymentId && returnedTickets.length > 0) {
        updateDoc(doc(firestoreProd, FIRESTORE_COLLECTION, deploymentId), {
          jiraTickets: returnedTickets,
          updatedAt: serverTimestamp(),
        }).catch(() => {});
      }

      setTagState({ status: "done", error: "", result: json, lines });
      return returnedTickets;
    } catch (err) {
      setTagState({
        status: "failed",
        error: err.message,
        result: null,
        lines: [{ text: `✖ ${err.message}`, type: "error", id: Math.random() }],
      });
      return null;
    }
  }, [prodLabel, deploymentId]);

  const runTestingGuide = useCallback(async (ticketsOverride) => {
    // Prefer tickets passed directly from the just-completed tagging step.
    // Relying on `jiraTickets` state here is unsafe inside the one-click
    // orchestrator: the `setJiraTickets` call in `runTagging` has not yet
    // re-rendered, so this closure would still see the stale (empty) value
    // and the backend would fall back to the lag-prone label-based JQL.
    const ticketsToUse = Array.isArray(ticketsOverride)
      ? ticketsOverride
      : jiraTickets;
    setGuideState((p) => ({ ...p, status: "running", error: "" }));
    try {
      const res = await fetch(TESTING_GUIDE_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Forward the full ticket manifest when we have it — the backend
        // will use it directly and skip the lag-prone label-based JQL search.
        body: JSON.stringify({
          prs: prs || [],
          label: prodLabel,
          tickets: ticketsToUse,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setGuideState({
        status: "done",
        error: "",
        text: json.testingGuide || "",
        docUrl: json.docUrl || TESTING_PLAYBOOK_DOC_URL,
        prCount: json.prCount || 0,
        jiraStoryCount: json.jiraStoryCount || 0,
      });
      return true;
    } catch (err) {
      setGuideState((p) => ({ ...p, status: "failed", error: err.message }));
      return false;
    }
  }, [prs, prodLabel, jiraTickets]);

  const runChangelog = useCallback(async (ticketsOverride) => {
    // Prefer tickets passed directly from the just-completed tagging step.
    // See runTestingGuide for why reading `jiraTickets` state here would be
    // stale inside the one-click orchestrator and yield an empty changelog.
    const ticketsToUse = Array.isArray(ticketsOverride)
      ? ticketsOverride
      : jiraTickets;
    setChangelogState((p) => ({ ...p, status: "running", error: "" }));
    try {
      const res = await fetch(CHANGELOG_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label: prodLabel,
          tickets: ticketsToUse,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const complete = json.changelogComplete || json.changelog || "";
      const client = json.changelogClient || json.changelogSimple || "";
      setChangelogState({
        status: "done",
        error: "",
        complete,
        client,
        ticketCount: json.ticketCount || 0,
      });

      if (deploymentId && (complete || client)) {
        updateDoc(doc(firestoreProd, FIRESTORE_COLLECTION, deploymentId), {
          changelogComplete: complete,
          changelogClient: client,
          "checks.changelog": true,
        }).catch(() => {});
      }
      onComplete?.({ changelogComplete: complete, changelogClient: client });
      return true;
    } catch (err) {
      setChangelogState((p) => ({ ...p, status: "failed", error: err.message }));
      return false;
    }
  }, [prodLabel, deploymentId, onComplete, jiraTickets]);

  // One-click orchestrator: runs all three in sequence. Each step is
  // independent — if an earlier one fails we still attempt the later ones
  // because tagging is only strictly required for accurate downstream data,
  // but AI-based guide/changelog can still produce useful output without it.
  const runAll = useCallback(async () => {
    setOverallRunning(true);
    try {
      const taggedTickets = await runTagging();
      // Forward the freshly-resolved ticket manifest directly to both
      // downstream steps. This avoids the stale-state read that previously
      // sent an empty ticket list on the first run and produced an empty
      // changelog. If tagging failed, fall back to whatever is in state.
      const ticketsForRun = Array.isArray(taggedTickets)
        ? taggedTickets
        : jiraTickets;
      // Testing guide + changelog can safely run in parallel — both receive
      // the resolved manifest directly and do not depend on each other.
      await Promise.all([
        runTestingGuide(ticketsForRun),
        runChangelog(ticketsForRun),
      ]);
    } finally {
      setOverallRunning(false);
    }
  }, [runTagging, runTestingGuide, runChangelog, jiraTickets]);

  const hasAnyOutput =
    tagState.status !== "idle" ||
    guideState.status !== "idle" ||
    changelogState.status !== "idle";

  const anyFailed =
    tagState.status === "failed" ||
    guideState.status === "failed" ||
    changelogState.status === "failed";

  const allDone =
    tagState.status === "done" &&
    guideState.status === "done" &&
    changelogState.status === "done";

  const mainButtonLabel = (() => {
    if (overallRunning) return "Running release automation…";
    if (anyFailed) return "↺ Retry Failed Steps";
    if (allDone) return "↺ Re-run Everything";
    if (hasAnyOutput) return "▶ Continue";
    return "▶ Run Release Automation";
  })();

  const onMainClick = () => {
    if (overallRunning) return;
    // If we already have some output and only some failed, just retry those.
    if (anyFailed && !allDone) {
      (async () => {
        setOverallRunning(true);
        try {
          const tasks = [];
          if (tagState.status !== "done") tasks.push(runTagging);
          if (guideState.status !== "done") tasks.push(runTestingGuide);
          if (changelogState.status !== "done") tasks.push(runChangelog);
          // Keep tagging first (others depend on its output); run remaining in parallel.
          if (tasks[0] === runTagging) {
            const taggedTickets = await runTagging();
            const ticketsForRun = Array.isArray(taggedTickets)
              ? taggedTickets
              : jiraTickets;
            await Promise.all(tasks.slice(1).map((fn) => fn(ticketsForRun)));
          } else {
            await Promise.all(tasks.map((fn) => fn()));
          }
        } finally {
          setOverallRunning(false);
        }
      })();
      return;
    }
    runAll();
  };

  return (
    <div className={`prod-step-card p-3 mb-3 ${isDone ? "prod-step-card--done" : ""}`}>
      <div className="d-flex align-items-start gap-3">
        <StepNumber n={4} status={isDone ? "passed" : overallRunning ? "running" : "idle"} />
        <div className="flex-grow-1">
          <div className="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-2">
            <div className="fw-semibold">Release Automation — Jira, Playbook & Changelog</div>
            <MarkPassedBtn done={isDone} onMark={markPassed} />
          </div>
          <div className="text-muted mb-3" style={{ fontSize: "0.8rem" }}>
            One click tags this release's Jira stories with{" "}
            <code className="bg-light px-1 rounded">{prodLabel}</code>, generates a
            focused <strong>testing playbook</strong> linked to the exact tickets
            shipped, and produces both <strong>internal</strong> and{" "}
            <strong>client-facing</strong> changelogs.
          </div>

          {/* Main one-click runner */}
          <div className="dep-release-runner mb-3">
            <button
              className={`btn ${allDone ? "btn-outline-primary" : "btn-danger"} dep-release-runner-btn`}
              onClick={onMainClick}
              disabled={overallRunning}
            >
              {overallRunning ? <><span className="prod-spinner me-2" />{mainButtonLabel}</> : mainButtonLabel}
            </button>
            <div className="dep-release-runner-hint">
              Label: <code>{prodLabel}</code>
              {prs?.filter((p) => p.prUrl).length
                ? ` · ${prs.filter((p) => p.prUrl).length} PR${prs.filter((p) => p.prUrl).length !== 1 ? "s" : ""} linked`
                : ` · No PR URLs — add them in step 3 for ticket-linked tests`}
            </div>
          </div>

          {/* Per-substep progress */}
          <div className="dep-release-rows">
            <ReleaseSubstepRow
              icon="🏷️"
              title="Tag Jira Stories"
              subtitle={
                tagState.status === "done" && tagState.result
                  ? `${tagState.result.updated} tagged · ${tagState.result.skipped} already labelled`
                  : `Label in-sprint stories with ${prodLabel}`
              }
              status={tagState.status}
              error={tagState.error}
              onRetry={runTagging}
            >
              {tagState.lines.length > 0 && (
                <div className="prod-terminal" style={{ minHeight: 60, maxHeight: 160 }}>
                  {tagState.lines.map((l) => (
                    <span key={l.id} className={`prod-terminal-line ${l.type}`}>{l.text}{"\n"}</span>
                  ))}
                </div>
              )}
            </ReleaseSubstepRow>

            <ReleaseSubstepRow
              icon="📋"
              title="Testing Playbook"
              subtitle={
                guideState.status === "done"
                  ? `${guideState.jiraStoryCount} ticket${guideState.jiraStoryCount !== 1 ? "s" : ""} · ${guideState.prCount} PR${guideState.prCount !== 1 ? "s" : ""} · priority-ordered, Jira-linked`
                  : "Focused QA checklist — one test per ticket, ranked by priority, each linked to its Jira issue"
              }
              status={guideState.status}
              error={guideState.error}
              onRetry={runTestingGuide}
              rightSlot={
                guideState.status === "done" && guideState.docUrl ? (
                  <a href={guideState.docUrl} target="_blank" rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-success">Open Doc ↗</a>
                ) : null
              }
            >
              {guideState.text && (
                <>
                  <div className="prod-guide-preview">
                    {guideState.text.split("\n").map((line, i) => {
                      const trimmed = line.trim();
                      if (!trimmed) return <div key={i} style={{ height: "0.4rem" }} />;
                      if (/^#{1,3}\s/.test(trimmed)) {
                        return (
                          <div key={i} className="prod-guide-section-header">
                            {trimmed.replace(/^#{1,3}\s*/, "").replace(/\*\*/g, "")}
                          </div>
                        );
                      }
                      if (/^\d+\./.test(trimmed) || /^[-•]/.test(trimmed)) {
                        const cleaned = trimmed.replace(/^\d+\.\s*/, "").replace(/^[-•]\s*/, "");
                        return (
                          <div key={i} className="prod-guide-item">
                            <span className="prod-guide-item-icon">☐</span>
                            <span dangerouslySetInnerHTML={{
                              __html: cleaned
                                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
                                .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>"),
                            }} />
                          </div>
                        );
                      }
                      if (/^_.+_$/.test(trimmed)) {
                        return <div key={i} className="text-muted" style={{ fontSize: "0.78rem", fontStyle: "italic" }}>
                          {trimmed.replace(/^_|_$/g, "")}
                        </div>;
                      }
                      return <div key={i} className="prod-guide-text">{trimmed}</div>;
                    })}
                  </div>
                  <div className="d-flex gap-2 mt-2">
                    <button className="btn btn-sm btn-outline-secondary"
                      onClick={() => navigator.clipboard.writeText(guideState.text)}>Copy</button>
                    {guideState.docUrl && (
                      <a href={guideState.docUrl} target="_blank" rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary">Open in Google Docs ↗</a>
                    )}
                  </div>
                </>
              )}
            </ReleaseSubstepRow>

            <ReleaseSubstepRow
              icon="📝"
              title="Changelogs"
              subtitle={
                changelogState.status === "done"
                  ? `${changelogState.ticketCount || 0} tickets · internal + client-facing generated`
                  : "AI writes both an internal changelog and a simplified client-facing version"
              }
              status={changelogState.status}
              error={changelogState.error}
              onRetry={runChangelog}
            >
              {(changelogState.complete || changelogState.client) && (
                <>
                  <div className="dep-changelog-tabs mb-2">
                    <button className={`dep-changelog-tab ${changelogTab === "complete" ? "active" : ""}`}
                      onClick={() => setChangelogTab("complete")}>Complete (Internal)</button>
                    <button className={`dep-changelog-tab ${changelogTab === "client" ? "active" : ""}`}
                      onClick={() => setChangelogTab("client")}>Client-Facing</button>
                  </div>
                  {changelogTab === "complete" && changelogState.complete && (
                    <>
                      <MarkdownBlock content={changelogState.complete} />
                      <button className="btn btn-sm btn-outline-secondary mt-2"
                        onClick={() => navigator.clipboard.writeText(changelogState.complete)}>Copy</button>
                    </>
                  )}
                  {changelogTab === "client" && changelogState.client && (
                    <>
                      <MarkdownBlock content={changelogState.client} />
                      <button className="btn btn-sm btn-outline-secondary mt-2"
                        onClick={() => navigator.clipboard.writeText(changelogState.client)}>Copy</button>
                    </>
                  )}
                </>
              )}
            </ReleaseSubstepRow>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── New / Resume Deployment Wizard ──────────────────────────────────────────

function NewDeploymentWizard({ onSaved, onCancel, existingDeployment }) {
  const isResume = !!existingDeployment;

  const [deploymentId, setDeploymentId] = useState(existingDeployment?.id || null);
  const [label] = useState(() => existingDeployment?.label || todayLabel());
  const [checks, setChecks] = useState(() => ({
    smokeTest: false, e2e: false, prsMerged: false, meeting: false, changelog: false,
    ...(existingDeployment?.checks || {}),
  }));
  const [currentPrs, setCurrentPrs] = useState(existingDeployment?.prs || []);
  const [currentIssues, setCurrentIssues] = useState(existingDeployment?.issues || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Only create a new Firestore doc when starting fresh (not resuming)
    if (isResume) return;
    (async () => {
      try {
        const docRef = await addDoc(collection(firestoreProd, FIRESTORE_COLLECTION), {
          label,
          status: "inprogress",
          checks: { smokeTest: false, e2e: false, prsMerged: false, meeting: false, changelog: false },
          prs: [],
          issues: [],
          changelogComplete: "",
          changelogClient: "",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setDeploymentId(docRef.id);
      } catch (err) {
        console.error("Failed to create deployment doc:", err);
      }
    })();
  }, []); // eslint-disable-line

  const updateCheck = useCallback(async (key, value = true) => {
    setChecks((p) => {
      const next = { ...p, [key]: value };
      if (deploymentId) {
        updateDoc(doc(firestoreProd, FIRESTORE_COLLECTION, deploymentId), {
          [`checks.${key}`]: value,
          updatedAt: serverTimestamp(),
        }).catch(() => {});
      }
      return next;
    });
  }, [deploymentId]);

  const handlePRsChange = useCallback((updatedPrs) => {
    setCurrentPrs(updatedPrs);
    if (deploymentId) {
      updateDoc(doc(firestoreProd, FIRESTORE_COLLECTION, deploymentId), {
        prs: updatedPrs,
        updatedAt: serverTimestamp(),
      }).catch(() => {});
    }
  }, [deploymentId]);

  const finalize = async () => {
    if (!deploymentId) return;
    setSaving(true);
    try {
      await updateDoc(doc(firestoreProd, FIRESTORE_COLLECTION, deploymentId), {
        status: "completed",
        checks,
        updatedAt: serverTimestamp(),
      });
      onSaved?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Wizard has 4 logical steps: Testing (= smokeTest + e2e), PRs, Meeting, Changelog.
  // The underlying Firestore doc still stores 5 check keys for backward
  // compatibility with the dashboard cards, analytics, and detail modal.
  const testingDone = !!(checks.smokeTest && checks.e2e);
  const doneCount =
    [testingDone, checks.prsMerged, checks.meeting, checks.changelog].filter(Boolean).length;
  const allDone = doneCount === 4;

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <div>
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-sm btn-outline-secondary" onClick={onCancel}>← Back</button>
            <h5 className="mb-0 fw-bold">
              {isResume ? "Resume Deployment" : "New Deployment"} — {label}
            </h5>
            {isResume && (
              <span className="badge" style={{ background: "#dbeafe", color: "#1e40af", fontSize: "0.7rem" }}>
                Resuming
              </span>
            )}
          </div>
          <p className="text-muted mb-0 mt-1" style={{ fontSize: "0.8rem" }}>
            {isResume
              ? "Picking up from where you left off. Completed steps are pre-filled."
              : "Complete all steps in order. Progress is auto-saved."}
          </p>
        </div>
        <a href={TESTING_HANDBOOK_URL} target="_blank" rel="noopener noreferrer"
          className="btn btn-sm btn-outline-primary">📋 Testing Handbook ↗</a>
      </div>

      {/* Progress tracker — "Testing" combines the smokeTest + e2e checks
          that are now driven by a single GitHub Actions workflow. */}
      <div className="dep-wizard-progress mb-4">
        {[
          { key: "testing",   label: "Testing",    done: checks.smokeTest && checks.e2e },
          { key: "prsMerged", label: "PRs Merged", done: !!checks.prsMerged },
          { key: "meeting",   label: "Meeting",    done: !!checks.meeting },
          { key: "changelog", label: "Changelog",  done: !!checks.changelog },
        ].map(({ key, label: lbl, done }, i, arr) => (
          <React.Fragment key={key}>
            <div className={`dep-wizard-step ${done ? "done" : "pending"}`}>
              <div className="dep-wizard-step-dot">{done ? "✓" : i + 1}</div>
              <div className="dep-wizard-step-label">{lbl}</div>
            </div>
            {i < arr.length - 1 && <div className={`dep-wizard-connector ${done ? "done" : ""}`} />}
          </React.Fragment>
        ))}
      </div>

      <ProdDeploymentTestingStep
        onComplete={() => { updateCheck("smokeTest"); updateCheck("e2e"); }}
        initialDone={checks.smokeTest && checks.e2e}
      />
      <PRChecklistStep
        onComplete={() => updateCheck("prsMerged")}
        onChange={handlePRsChange}
        initialDone={checks.prsMerged}
        initialPrs={currentPrs}
      />
      <DeploymentMeetingStep
        onComplete={() => updateCheck("meeting")}
        initialDone={checks.meeting}
      />
      <ChangelogStep
        deploymentId={deploymentId}
        label={label}
        prs={currentPrs}
        onComplete={() => updateCheck("changelog")}
        initialDone={checks.changelog}
        initialChangelogs={{
          complete: existingDeployment?.changelogComplete || "",
          client:   existingDeployment?.changelogClient  || "",
        }}
        initialJiraTickets={existingDeployment?.jiraTickets || []}
      />

      {/* Issues section */}
      <div className="prod-step-card p-3 mb-3">
        <IssuesSection
          issues={currentIssues}
          deploymentId={deploymentId}
          onChange={setCurrentIssues}
        />
      </div>

      {/* Finalize bar — always visible, warns if not all done */}
      <div className="dep-finalize-bar">
        <div style={{ fontSize: "0.85rem" }}>
          {allDone
            ? <span className="text-success fw-semibold">All checks complete — ready to finalize.</span>
            : <span className="text-muted">{doneCount}/4 checks complete. You can still finalize early.</span>
          }
        </div>
        <button className="btn btn-success" onClick={finalize} disabled={saving}>
          {saving ? <><span className="prod-spinner me-1" />Saving…</> : "✓ Finalize Deployment"}
        </button>
      </div>
    </div>
  );
}

// ─── Analytics helpers ────────────────────────────────────────────────────────

function getWeekKey(ts) {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  // ISO week: Monday-based
  const day = d.getDay() || 7;
  const monday = new Date(d);
  monday.setDate(d.getDate() - day + 1);
  return monday.toISOString().slice(0, 10); // YYYY-MM-DD of that Monday
}

function fmtWeek(isoMonday) {
  const d = new Date(isoMonday);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function buildAnalytics(deployments) {
  const completed = deployments.filter((d) => d.status === "completed");
  const total = completed.length;

  // Check completion rates across completed deployments
  const CHECK_KEYS = ["smokeTest", "e2e", "prsMerged", "meeting", "changelog"];
  const CHECK_LABELS = {
    smokeTest: "Smoke Test", e2e: "E2E", prsMerged: "PRs",
    meeting: "Meeting", changelog: "Changelog",
  };
  const checkRates = CHECK_KEYS.map((key) => {
    const done = completed.filter((d) => d.checks?.[key]).length;
    return { key, label: CHECK_LABELS[key], done, pct: total ? Math.round((done / total) * 100) : 0 };
  });

  // Deployments per week (last 8 weeks)
  const weekMap = {};
  completed.forEach((d) => {
    const wk = getWeekKey(d.createdAt);
    if (wk) weekMap[wk] = (weekMap[wk] || 0) + 1;
  });
  const now = new Date();
  const weeks = [];
  for (let i = 7; i >= 0; i--) {
    const monday = new Date(now);
    const day = monday.getDay() || 7;
    monday.setDate(monday.getDate() - day + 1 - i * 7);
    const key = monday.toISOString().slice(0, 10);
    weeks.push({ key, label: fmtWeek(key), count: weekMap[key] || 0 });
  }
  const maxWeekCount = Math.max(...weeks.map((w) => w.count), 1);

  // Average checks per deployment
  const avgChecks = total
    ? (completed.reduce((sum, d) => sum + Object.values(d.checks || {}).filter(Boolean).length, 0) / total).toFixed(1)
    : "—";

  // Streak: consecutive weeks with at least one deployment
  let streak = 0;
  for (let i = weeks.length - 1; i >= 0; i--) {
    if (weeks[i].count > 0) streak++;
    else break;
  }

  // Most recent completed
  const lastDep = completed[0];
  const lastDate = lastDep ? fmtDate(lastDep.createdAt) : "—";

  // PRs with URLs across all deployments
  const prUrlCount = completed.reduce((sum, d) => {
    return sum + (d.prs || []).filter((p) => p.prUrl).length;
  }, 0);

  return { total, checkRates, weeks, maxWeekCount, avgChecks, streak, lastDate, prUrlCount };
}

// ─── Analytics Panel ──────────────────────────────────────────────────────────

function AnalyticsPanel({ deployments }) {
  const a = buildAnalytics(deployments);

  return (
    <div className="dash-analytics mb-4">
      {/* Stat cards row */}
      <div className="dash-stat-row mb-3">
        <div className="dash-stat-card">
          <div className="dash-stat-label">Total Deployments</div>
          <div className="dash-stat-value">{a.total}</div>
          <div className="dash-stat-sub">completed</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-label">Last Deployment</div>
          <div className="dash-stat-value" style={{ fontSize: "1.1rem" }}>{a.lastDate}</div>
          <div className="dash-stat-sub">most recent</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-label">Avg Checks / Deploy</div>
          <div className="dash-stat-value">{a.avgChecks}<span style={{ fontSize: "1rem", fontWeight: 400 }}>/5</span></div>
          <div className="dash-stat-sub">completeness</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-label">Active Streak</div>
          <div className="dash-stat-value">{a.streak}<span style={{ fontSize: "1rem", fontWeight: 400 }}> wk</span></div>
          <div className="dash-stat-sub">consecutive weeks</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-label">PRs Tracked</div>
          <div className="dash-stat-value">{a.prUrlCount}</div>
          <div className="dash-stat-sub">with URL</div>
        </div>
      </div>

      <div className="dash-bottom-row">
        {/* Weekly cadence bar chart */}
        <div className="dash-panel">
          <div className="dash-panel-title">Deployments per Week</div>
          <div className="dash-bar-chart">
            {a.weeks.map((w) => (
              <div key={w.key} className="dash-bar-col">
                <div className="dash-bar-wrap">
                  <div
                    className="dash-bar-fill"
                    style={{ height: `${(w.count / a.maxWeekCount) * 100}%` }}
                  />
                </div>
                <div className="dash-bar-count">{w.count || ""}</div>
                <div className="dash-bar-label">{w.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Check completion rates */}
        <div className="dash-panel">
          <div className="dash-panel-title">Check Completion Rate</div>
          <div className="d-flex flex-column gap-2 mt-1">
            {a.checkRates.map((c) => (
              <div key={c.key}>
                <div className="d-flex justify-content-between mb-1" style={{ fontSize: "0.78rem" }}>
                  <span className="fw-medium">{c.label}</span>
                  <span className="text-muted">{c.done}/{a.total} ({c.pct}%)</span>
                </div>
                <div className="dash-check-bar">
                  <div
                    className={`dash-check-fill ${c.pct === 100 ? "perfect" : c.pct >= 75 ? "good" : c.pct >= 50 ? "ok" : "low"}`}
                    style={{ width: `${c.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          {a.total > 0 && (
            <div className="dash-insight mt-3">
              {(() => {
                const worst = [...a.checkRates].sort((a, b) => a.pct - b.pct)[0];
                if (worst.pct < 80) {
                  return `💡 "${worst.label}" is the least completed check (${worst.pct}%). Consider making it mandatory.`;
                }
                return "✅ All checks are well-covered across deployments.";
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Deployment Timeline ──────────────────────────────────────────────────────

function DeploymentTimeline({ deployments, onOpen, onContinue, onDelete }) {
  // Group completed deployments by week
  const groups = {};
  deployments.filter((d) => d.status === "completed").forEach((d) => {
    const wk = getWeekKey(d.createdAt) || "Unknown";
    if (!groups[wk]) groups[wk] = [];
    groups[wk].push(d);
  });

  const sortedWeeks = Object.keys(groups).sort((a, b) => b.localeCompare(a));

  if (sortedWeeks.length === 0) return null;

  return (
    <div className="mb-4">
      <div className="prod-section-header mb-3">Deployment History</div>
      {sortedWeeks.map((wk) => (
        <div key={wk} className="dash-timeline-week">
          <div className="dash-timeline-week-label">
            Week of {fmtWeek(wk)}
            <span className="dash-timeline-week-count">{groups[wk].length} deployment{groups[wk].length !== 1 ? "s" : ""}</span>
          </div>
          <div className="dep-grid">
            {groups[wk].map((d) => (
              <DeploymentCard
                key={d.id} dep={d}
                onOpen={onOpen}
                onContinue={onContinue}
                onDelete={onDelete}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Dashboard (history) ──────────────────────────────────────────────────────

function DeploymentDashboard({ onNew, onContinue }) {
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const loadDeployments = async () => {
    try {
      const q = query(collection(firestoreProd, FIRESTORE_COLLECTION), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setDeployments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Failed to load deployments:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDeployments(); }, []); // eslint-disable-line

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(firestoreProd, FIRESTORE_COLLECTION, id));
      setDeployments((prev) => prev.filter((d) => d.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (err) {
      console.error("Failed to delete deployment:", err);
    }
  };

  const inProgress = deployments.filter((d) => d.status !== "completed");

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <div>
          <h4 className="mb-0 fw-bold">Production Deployments</h4>
          <p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
            Track deployment history, analytics, and changelogs.
          </p>
        </div>
        <div className="d-flex gap-2">
          <a href={TESTING_HANDBOOK_URL} target="_blank" rel="noopener noreferrer"
            className="btn btn-sm btn-outline-primary">📋 Handbook ↗</a>
          <button className="btn btn-sm btn-primary" onClick={onNew}>+ New Deployment</button>
        </div>
      </div>

      {loading ? (
        <div className="d-flex align-items-center gap-2 text-muted py-4">
          <span className="prod-spinner" /> Loading deployments…
        </div>
      ) : deployments.length === 0 ? (
        <div className="dep-empty-state">
          <div style={{ fontSize: "2.5rem" }}>🚀</div>
          <div className="fw-semibold mt-2">No deployments yet</div>
          <div className="text-muted" style={{ fontSize: "0.85rem" }}>Create your first deployment to start tracking.</div>
          <button className="btn btn-primary btn-sm mt-3" onClick={onNew}>+ New Deployment</button>
        </div>
      ) : (
        <>
          {/* Analytics — only shown when there's at least one completed deployment */}
          {deployments.some((d) => d.status === "completed") && (
            <AnalyticsPanel deployments={deployments} />
          )}

          {/* In-progress */}
          {inProgress.length > 0 && (
            <div className="mb-4">
              <div className="prod-section-header mb-2">In Progress</div>
              <div className="dep-grid">
                {inProgress.map((d) => (
                  <DeploymentCard
                    key={d.id} dep={d}
                    onOpen={setSelected}
                    onContinue={onContinue}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Timeline grouped by week */}
          <DeploymentTimeline
            deployments={deployments}
            onOpen={setSelected}
            onContinue={onContinue}
            onDelete={handleDelete}
          />
        </>
      )}

      {selected && (
        <DeploymentDetailModal
          dep={selected}
          onClose={() => setSelected(null)}
          onIssuesChange={(id, updated) => {
            setDeployments((prev) =>
              prev.map((d) => d.id === id ? { ...d, issues: updated } : d)
            );
            setSelected((prev) => prev ? { ...prev, issues: updated } : prev);
          }}
        />
      )}
    </div>
  );
}

// ─── Root Page ────────────────────────────────────────────────────────────────

export default function ProdDeploymentPage() {
  const [view, setView] = useState("dashboard"); // "dashboard" | "new" | "resume"
  const [resumeDeployment, setResumeDeployment] = useState(null);

  const handleContinue = (dep) => {
    setResumeDeployment(dep);
    setView("resume");
  };

  const handleBack = () => {
    setResumeDeployment(null);
    setView("dashboard");
  };

  return (
    <div className="ProdDeploymentContainer">
      <div className="container-fluid py-4 px-4">
        {view === "dashboard" ? (
          <DeploymentDashboard
            onNew={() => setView("new")}
            onContinue={handleContinue}
          />
        ) : (
          <NewDeploymentWizard
            onSaved={handleBack}
            onCancel={handleBack}
            existingDeployment={view === "resume" ? resumeDeployment : null}
          />
        )}
      </div>
    </div>
  );
}
